import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetPublicProgramsQuery,
  useGetPublicCountriesQuery,
  useGetPublicSubjectsQuery,
  useGetPublicUniversitiesQuery,
} from "../../app/features/program/programApi";

const PAGE_SIZE = 9;

const PROGRAM_TYPES = [
  { value: "", label: "All Types" },
  { value: "b", label: "Bachelor" },
  { value: "pb", label: "Pathway Bachelor" },
  { value: "m", label: "Master" },
  { value: "d", label: "Diploma" },
  { value: "ud", label: "Undergraduate Diploma" },
  { value: "p", label: "PGD" },
];

const ORDERING_OPTIONS = [
  { value: "", label: "Most Recent" },
  { value: "tuition_fee", label: "Tuition: Low to High" },
  { value: "-tuition_fee", label: "Tuition: High to Low" },
  { value: "application_end", label: "Deadline: Soonest" },
];

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const COUNTRY_ISO = {
  Finland: "fi", Spain: "es", Cyprus: "cy", Malta: "mt", Germany: "de",
  France: "fr", Italy: "it", Netherlands: "nl", Sweden: "se", Norway: "no",
  Denmark: "dk", Poland: "pl", Portugal: "pt", Austria: "at", Belgium: "be",
  Switzerland: "ch", Greece: "gr", "Czech Republic": "cz", Hungary: "hu",
  Romania: "ro", Ireland: "ie", Estonia: "ee", Latvia: "lv", Lithuania: "lt",
  Slovakia: "sk", Slovenia: "si", Croatia: "hr", Luxembourg: "lu", "United Kingdom": "gb",
};

function FlagIcon({ country, iso: isoProp }) {
  const iso = isoProp || COUNTRY_ISO[country];
  return (
    <div className="p-2 bg-white rounded-lg shadow-sm inline-flex items-center justify-center">
      {iso ? (
        <span className={`fi fi-${iso}`} style={{ width: 32, height: 22, borderRadius: 3, display: "block" }} />
      ) : (
        <span className="material-symbols-outlined text-[28px] text-purple-300">flag</span>
      )}
    </div>
  );
}

function BadgePill({ scholarship }) {
  if (!scholarship) return null;
  const lower = scholarship.toLowerCase();
  if (lower.includes("full") || lower.includes("100"))
    return <span className="px-2 py-1 bg-green-100 text-green-600 text-[10px] font-bold rounded uppercase tracking-wider">Full Scholarship</span>;
  return <span className="px-2 py-1 bg-green-100 text-green-600 text-[10px] font-bold rounded uppercase tracking-wider">Scholarship Available</span>;
}

function LanguageTag({ program }) {
  if (program.ielts_overall) return `IELTS ${program.ielts_overall}`;
  if (program.pte_overall) return `PTE ${program.pte_overall}`;
  if (program.toefl_overall) return `TOEFL ${program.toefl_overall}`;
  if (program.duolingo_overall) return `Duolingo ${program.duolingo_overall}`;
  return "No language requirement";
}

function ProgramCard({ program, iso }) {
  const navigate = useNavigate();
  const deadline = program.application_end
    ? new Date(program.application_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "Open";
  const tuition = program.tuition_fee ? `€${program.tuition_fee.toLocaleString()} / Academic Year` : "Contact for fees";
  const duration = program.duration ? `${program.duration} Years` : "—";

  return (
    <div
      className="group bg-[#F5F3FF] p-6 rounded-2xl border border-purple-100 hover:border-purple-400 transition-all duration-300 flex flex-col"
      style={{ boxShadow: "0 4px 20px -2px rgba(139, 92, 246, 0.1)" }}
    >
      <div className="flex justify-between items-start mb-4">
        <FlagIcon country={program.country} iso={iso} />
        <BadgePill scholarship={program.scholarship} />
      </div>
      <h3 className="font-montserrat text-lg font-bold text-[#4C1D95] mb-1 group-hover:text-[#8B5CF6] transition-colors leading-tight">
        {program.program_type_display} in {program.subject_name}
      </h3>
      <p className="text-xs text-slate-500 font-medium mb-6">{program.city}, {program.country}</p>
      <div className="space-y-3 flex-grow">
        <div className="flex items-center text-xs text-slate-600">
          <span className="material-symbols-outlined text-[18px] mr-3 text-[#8B5CF6]">schedule</span>
          {duration} • Full-time
        </div>
        <div className="flex items-center text-xs text-slate-600">
          <span className="material-symbols-outlined text-[18px] mr-3 text-[#8B5CF6]">event_note</span>
          Deadline: {deadline}
        </div>
        <div className="flex items-center text-xs font-semibold text-[#4C1D95]">
          <span className="material-symbols-outlined text-[18px] mr-3 text-[#8B5CF6]">payments</span>
          {tuition}
        </div>
        <div className="flex items-center text-xs text-slate-600">
          <span className="material-symbols-outlined text-[18px] mr-3 text-[#8B5CF6]">translate</span>
          <LanguageTag program={program} />
        </div>
      </div>
      <button
        onClick={() => navigate(`/programs/${program.slug || program.id}`)}
        className="mt-8 w-full py-3 text-white font-bold rounded-xl shadow-lg shadow-purple-200 hover:shadow-purple-400 transition-all"
        style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)" }}
      >
        See More Details
      </button>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-[#F5F3FF] p-6 rounded-2xl border border-purple-100 flex flex-col gap-4 animate-pulse">
      <div className="flex justify-between">
        <div className="w-12 h-9 bg-purple-200 rounded-lg" />
        <div className="w-24 h-5 bg-purple-200 rounded" />
      </div>
      <div className="w-3/4 h-5 bg-purple-200 rounded" />
      <div className="w-1/2 h-3 bg-purple-100 rounded" />
      <div className="space-y-2 flex-grow">
        {[...Array(4)].map((_, i) => <div key={i} className="w-full h-3 bg-purple-100 rounded" />)}
      </div>
      <div className="w-full h-10 bg-purple-200 rounded-xl mt-4" />
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) pages.push(i);
    else if (pages[pages.length - 1] !== "...") pages.push("...");
  }
  return (
    <div className="mt-12 flex items-center justify-center space-x-2">
      <button onClick={() => onChange(page - 1)} disabled={page === 1} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
        <span className="material-symbols-outlined text-sm">chevron_left</span>
      </button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={i} className="text-slate-400 w-10 text-center">...</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-10 h-10 rounded-lg font-bold transition-colors ${p === page ? "bg-[#8B5CF6] text-white" : "hover:bg-slate-100 text-slate-700"}`}
          >
            {p}
          </button>
        )
      )}
      <button onClick={() => onChange(page + 1)} disabled={page === totalPages} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
        <span className="material-symbols-outlined text-sm">chevron_right</span>
      </button>
    </div>
  );
}

// ── University Card ───────────────────────────────────────────────────────────
function UniversityCard({ uni, iso: isoProp }) {
  const iso = isoProp || COUNTRY_ISO[uni.country];
  return (
    <div
      className="bg-white rounded-2xl border border-slate-100 hover:border-purple-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
      style={{ boxShadow: "0 2px 12px -2px rgba(139, 92, 246, 0.08)" }}
    >
      {/* Banner image */}
      <div className="h-32 bg-gradient-to-br from-purple-100 to-purple-50 relative overflow-hidden">
        {uni.image ? (
          <img src={uni.image} alt={uni.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="material-symbols-outlined text-5xl text-purple-200">account_balance</span>
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-grow">
        {/* Logo + name */}
        <div className="flex items-center gap-3 mb-3">
          {uni.logo ? (
            <img src={uni.logo} alt={`${uni.name} logo`} className="w-10 h-10 object-contain rounded-lg border border-slate-100 flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <span className="text-[#8B5CF6] font-bold text-sm">{uni.name.charAt(0)}</span>
            </div>
          )}
          <h3 className="font-montserrat font-bold text-[#4C1D95] text-sm leading-tight line-clamp-2">{uni.name}</h3>
        </div>

        {/* Location */}
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-auto">
          {iso && <span className={`fi fi-${iso} rounded`} style={{ width: 18, height: 13, display: "inline-block", flexShrink: 0 }} />}
          <span>{uni.city}, {uni.country}</span>
        </div>
      </div>
    </div>
  );
}

function UniversitySkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse">
      <div className="h-32 bg-slate-100" />
      <div className="p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-200 rounded-lg flex-shrink-0" />
          <div className="flex-1 h-4 bg-slate-200 rounded" />
        </div>
        <div className="w-1/2 h-3 bg-slate-100 rounded" />
      </div>
    </div>
  );
}

// ── Universities Tab ──────────────────────────────────────────────────────────
function UniversitiesTab({ countries, countryIso = {} }) {
  const [uniSearch, setUniSearch] = useState("");
  const [uniCountry, setUniCountry] = useState("");
  const debouncedSearch = useDebounce(uniSearch, 400);

  const { data: universities = [], isLoading } = useGetPublicUniversitiesQuery({
    search: debouncedSearch,
    country: uniCountry,
  });

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Sidebar */}
      <aside className="w-full lg:w-72 flex-shrink-0">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-montserrat font-bold text-lg">Filters</h2>
            {(uniSearch || uniCountry) && (
              <button onClick={() => { setUniSearch(""); setUniCountry(""); }} className="text-xs text-[#8B5CF6] font-bold uppercase tracking-wider hover:text-[#6D28D9]">
                Reset
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={uniSearch}
              onChange={(e) => setUniSearch(e.target.value)}
              placeholder="Search universities..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] text-sm"
            />
            <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-sm">search</span>
          </div>

          {/* Country */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Country</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setUniCountry("")}
                className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl border-2 transition-all text-center ${uniCountry === "" ? "border-[#8B5CF6] bg-purple-50" : "border-slate-100 bg-slate-50 hover:border-purple-200"}`}
              >
                <div className={`w-8 h-6 rounded flex items-center justify-center text-sm font-bold ${uniCountry === "" ? "bg-[#8B5CF6] text-white" : "bg-slate-200 text-slate-500"}`}>All</div>
                <span className={`text-[10px] font-semibold ${uniCountry === "" ? "text-[#8B5CF6]" : "text-slate-500"}`}>All</span>
              </button>
              {countries.map((c) => {
                const iso = countryIso[c] || COUNTRY_ISO[c];
                const isSelected = uniCountry === c;
                return (
                  <button
                    key={c}
                    onClick={() => setUniCountry(c)}
                    className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl border-2 transition-all text-center ${isSelected ? "border-[#8B5CF6] bg-purple-50" : "border-slate-100 bg-slate-50 hover:border-purple-200"}`}
                  >
                    {iso ? (
                      <span className={`fi fi-${iso} rounded`} style={{ width: 32, height: 22, display: "block" }} />
                    ) : (
                      <div className="w-8 h-6 rounded bg-slate-200 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[14px] text-slate-400">flag</span>
                      </div>
                    )}
                    <span className={`text-[10px] font-semibold leading-tight line-clamp-2 ${isSelected ? "text-[#8B5CF6]" : "text-slate-500"}`}>{c}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </aside>

      {/* Grid */}
      <div className="flex-1">
        <p className="text-sm text-slate-500 font-medium mb-6">
          {isLoading ? (
            <span className="inline-block w-40 h-4 bg-slate-200 rounded animate-pulse" />
          ) : (
            <><span className="text-slate-900 font-semibold">{universities.length}</span> {universities.length === 1 ? "university" : "universities"}{uniCountry ? ` in ${uniCountry}` : ""}</>
          )}
        </p>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => <UniversitySkeletonCard key={i} />)}
          </div>
        ) : universities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <span className="material-symbols-outlined text-6xl mb-4">account_balance</span>
            <p className="text-lg font-semibold">No universities found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {universities.map((uni) => <UniversityCard key={uni.id} uni={uni} iso={countryIso[uni.country]} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Programs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "programs";

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [subject, setSubject] = useState("");
  const [type, setType] = useState("");
  const [tuitionMax, setTuitionMax] = useState(25000);
  const [ordering, setOrdering] = useState("");

  const debouncedSearch = useDebounce(search, 400);
  useEffect(() => { setPage(1); }, [debouncedSearch, country, subject, type, tuitionMax, ordering]);

  const queryArgs = {
    page, records: PAGE_SIZE, search: debouncedSearch,
    country, subject, type,
    tuition_max: tuitionMax < 25000 ? tuitionMax : "",
    ordering,
  };

  const { data, isLoading, isFetching } = useGetPublicProgramsQuery(queryArgs, { skip: activeTab !== "programs" });
  const { data: rawCountries = [] } = useGetPublicCountriesQuery();
  const countries = rawCountries.map((c) => (typeof c === "object" ? c.name : c));
  // Build a name → ISO lookup from the backend `flag` code (e.g. Uganda → "ug"),
  // falling back to the static COUNTRY_ISO map. This makes flags work for every
  // country that has data, not just the hardcoded European list.
  const countryIso = rawCountries.reduce((acc, c) => {
    if (c && typeof c === "object" && c.name) {
      acc[c.name] = (c.flag || COUNTRY_ISO[c.name] || "").toLowerCase();
    }
    return acc;
  }, { ...COUNTRY_ISO });
  const { data: subjects = [] } = useGetPublicSubjectsQuery();

  const programs = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const resetFilters = useCallback(() => {
    setSearch(""); setCountry(""); setSubject(""); setType(""); setTuitionMax(25000); setOrdering(""); setPage(1);
  }, []);

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, totalCount);

  const setTab = (tab) => setSearchParams({ tab });

  const tabs = [
    { key: "programs", label: "Programs", icon: "school" },
    { key: "universities", label: "Universities", icon: "account_balance" },
  ];

  return (
    <div className="bg-[#F9FAFB] font-poppins text-slate-800 min-h-screen">
      <main className="max-w-7xl mx-auto px-4 py-12">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white rounded-xl p-1 border border-slate-200 shadow-sm w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === tab.key
                  ? "bg-[#8B5CF6] text-white shadow-md shadow-purple-200"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Programs Tab */}
        {activeTab === "programs" && (
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-montserrat font-bold text-lg">Filters</h2>
                  <button onClick={resetFilters} className="text-xs text-[#8B5CF6] font-bold uppercase tracking-wider hover:text-[#6D28D9]">Reset All</button>
                </div>

                {/* Search */}
                <div className="relative mb-6">
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search programs..."
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] text-sm" />
                  <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-sm">search</span>
                </div>

                {/* Country */}
                <div className="mb-6">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Country</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => setCountry("")}
                      className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl border-2 transition-all text-center ${country === "" ? "border-[#8B5CF6] bg-purple-50 shadow-sm shadow-purple-100" : "border-slate-100 bg-slate-50 hover:border-purple-200 hover:bg-purple-50/50"}`}>
                      <div className={`w-8 h-6 rounded flex items-center justify-center text-sm font-bold ${country === "" ? "bg-[#8B5CF6] text-white" : "bg-slate-200 text-slate-500"}`}>All</div>
                      <span className={`text-[10px] font-semibold leading-tight ${country === "" ? "text-[#8B5CF6]" : "text-slate-500"}`}>All</span>
                    </button>
                    {countries.map((c) => {
                      const iso = countryIso[c];
                      const isSelected = country === c;
                      return (
                        <button key={c} onClick={() => setCountry(c)}
                          className={`flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl border-2 transition-all text-center ${isSelected ? "border-[#8B5CF6] bg-purple-50 shadow-sm shadow-purple-100" : "border-slate-100 bg-slate-50 hover:border-purple-200 hover:bg-purple-50/50"}`}>
                          {iso ? (
                            <span className={`fi fi-${iso} rounded`} style={{ width: 32, height: 22, display: "block", flexShrink: 0 }} />
                          ) : (
                            <div className="w-8 h-6 rounded bg-slate-200 flex items-center justify-center">
                              <span className="material-symbols-outlined text-[14px] text-slate-400">flag</span>
                            </div>
                          )}
                          <span className={`text-[10px] font-semibold leading-tight line-clamp-2 ${isSelected ? "text-[#8B5CF6]" : "text-slate-500"}`}>{c}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Subject */}
                <div className="mb-6">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Subject Area</label>
                  <select value={subject} onChange={(e) => setSubject(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]">
                    <option value="">All Subjects</option>
                    {subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>

                {/* Type */}
                <div className="mb-6">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Program Type</label>
                  <select value={type} onChange={(e) => setType(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]">
                    {PROGRAM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                {/* Tuition */}
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Max Tuition Fee</label>
                  <input type="range" min={0} max={25000} step={500} value={tuitionMax} onChange={(e) => setTuitionMax(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#8B5CF6]" />
                  <div className="flex justify-between mt-2 text-xs text-slate-500">
                    <span>€0</span>
                    <span className="font-semibold text-[#8B5CF6]">{tuitionMax >= 25000 ? "Any" : `€${tuitionMax.toLocaleString()}`}</span>
                    <span>€25,000+</span>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-6 rounded-2xl border border-purple-200">
                <h3 className="font-montserrat font-bold text-[#4C1D95] mb-2">Not sure where to start?</h3>
                <p className="text-sm text-slate-600 mb-4">Take our 2-minute eligibility test to see which programs you qualify for.</p>
                <button className="w-full py-2 bg-white text-[#8B5CF6] border border-[#8B5CF6] font-bold rounded-lg hover:bg-[#8B5CF6] hover:text-white transition-all text-sm">Start Test</button>
              </div>
            </aside>

            {/* Main */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-slate-500 font-medium">
                  {isLoading ? (
                    <span className="inline-block w-40 h-4 bg-slate-200 rounded animate-pulse" />
                  ) : (
                    <>Showing <span className="text-slate-900 font-semibold">{showingFrom}–{showingTo}</span> of <span className="text-slate-900 font-semibold">{totalCount}</span> programs{country ? ` in ${country}` : ""}</>
                  )}
                </p>
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-slate-500">Sort by:</span>
                  <select value={ordering} onChange={(e) => setOrdering(e.target.value)}
                    className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] cursor-pointer">
                    {ORDERING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              {isLoading || isFetching ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[...Array(PAGE_SIZE)].map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : programs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                  <span className="material-symbols-outlined text-6xl mb-4">search_off</span>
                  <p className="text-lg font-semibold">No programs found</p>
                  <p className="text-sm mt-1">Try adjusting your filters</p>
                  <button onClick={resetFilters} className="mt-4 px-5 py-2 text-sm font-bold text-[#8B5CF6] border border-[#8B5CF6] rounded-lg hover:bg-[#8B5CF6] hover:text-white transition-all">Clear Filters</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {programs.map((p) => <ProgramCard key={p.id} program={p} iso={countryIso[p.country]} />)}
                </div>
              )}

              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </div>
        )}

        {/* Universities Tab */}
        {activeTab === "universities" && <UniversitiesTab countries={countries} countryIso={countryIso} />}
      </main>
    </div>
  );
}
